import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHero, Section } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation"];

export default function ProviderApplication() {
  const nav = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [f, setF] = useState({
    provider_name: "", provider_type: "", city: "", state: "", service_area: "", private_pay_services: "",
    price_range: "", availability: "", capacity: "", contact_person: "", phone: "", email: "", website: "",
    consumer_profiles: "", preferred_referral_method: "", notes: "", company_website: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleService = (s) => setServices((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/providers/apply", { ...f, service_categories: services });
      setSubmitted(true);
      toast.success("Provider inquiry submitted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <Section className="py-24">
        <Card className="mx-auto max-w-xl p-10 text-center" data-testid="provider-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emeraldbrand" />
          <h2 className="mt-4 font-heading text-2xl font-semibold text-navy">Thank you</h2>
          <p className="mt-3 text-slate-500">
            Your provider inquiry has been received and will be reviewed by our team. Compensation agreements are never
            automatically approved.
          </p>
          <Button className="mt-6 bg-navy" onClick={() => nav("/providers")}>Back to provider network</Button>
        </Card>
      </Section>
    );
  }

  return (
    <div>
      <PageHero eyebrow="Provider onboarding" title="Become a participating provider"
        subtitle="Tell us about your services, geography, pricing, and available capacity. Our team reviews all submissions." />
      <Section className="py-16">
        <form onSubmit={submit} className="mx-auto max-w-3xl space-y-6" data-testid="provider-application-form">
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={f.company_website} onChange={set("company_website")}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          <Card className="p-6">
            <h3 className="font-heading text-lg font-semibold text-navy">Organization</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Provider name" required><Input data-testid="prov-name" required value={f.provider_name} onChange={set("provider_name")} /></Field>
              <Field label="Provider type"><Input data-testid="prov-type" value={f.provider_type} onChange={set("provider_type")} placeholder="e.g. Residential, In-Home, Center" /></Field>
              <Field label="City" required><Input data-testid="prov-city" required value={f.city} onChange={set("city")} /></Field>
              <Field label="State" required><Input data-testid="prov-state" required value={f.state} onChange={set("state")} /></Field>
              <Field label="Service area"><Input data-testid="prov-area" value={f.service_area} onChange={set("service_area")} /></Field>
              <Field label="General price range"><Input data-testid="prov-price" value={f.price_range} onChange={set("price_range")} /></Field>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Services offered</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`prov-service-${s}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      services.includes(s) ? "border-clinical bg-clinical text-white" : "border-slate-300 text-slate-600 hover:border-clinical"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-semibold text-navy">Capacity & contact</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Current availability"><Input data-testid="prov-availability" value={f.availability} onChange={set("availability")} /></Field>
              <Field label="Capacity"><Input data-testid="prov-capacity" value={f.capacity} onChange={set("capacity")} placeholder="e.g. 2 beds / 80 hrs" /></Field>
              <Field label="Contact person"><Input data-testid="prov-contact" value={f.contact_person} onChange={set("contact_person")} /></Field>
              <Field label="Phone"><Input data-testid="prov-phone" value={f.phone} onChange={set("phone")} /></Field>
              <Field label="Email"><Input data-testid="prov-email" type="email" value={f.email} onChange={set("email")} /></Field>
              <Field label="Website"><Input data-testid="prov-website" value={f.website} onChange={set("website")} /></Field>
              <Field label="Preferred referral method"><Input data-testid="prov-referral" value={f.preferred_referral_method} onChange={set("preferred_referral_method")} /></Field>
              <Field label="Private-pay services"><Input data-testid="prov-privatepay" value={f.private_pay_services} onChange={set("private_pay_services")} /></Field>
            </div>
            <div className="mt-4 grid gap-4">
              <Field label="Consumer profiles served"><Textarea data-testid="prov-profiles" value={f.consumer_profiles} onChange={set("consumer_profiles")} /></Field>
              <Field label="Notes"><Textarea data-testid="prov-notes" value={f.notes} onChange={set("notes")} /></Field>
            </div>
          </Card>

          <Button type="submit" size="lg" disabled={loading} data-testid="prov-submit-btn" className="w-full bg-navy hover:bg-navy/90">
            {loading ? "Submitting…" : "Submit provider inquiry"}
          </Button>
        </form>
      </Section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{required && " *"}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
